import styled from 'styled-components'

import accordionMinusIcon from 'assets/images/accordion-minus-icon.svg'
import accordionPlusIcon from 'assets/images/accordion-plus-icon.svg'

import { Color, MediaBreakpoint } from 'root-constants'

import {
  ConsentToggle,
  PrimaryButton,
  SecondaryButton,
  bodyTextStyles,
  centeredRightControlStyles,
  narrowContentStyles,
  sectionTitleStyles,
} from '../../CookieConsentShared.styles'
import { DESKTOP_CARD_WIDTH } from '../../constants'

const BaseCloseButton = styled.button`
  border: 0;

  background: transparent;

  cursor: pointer;
`

export const StyledCookieConsentPreferences = {
  PreferencesViewport: styled.div`
    width: 100%;
    min-height: 100%;

    display: flex;
    flex-direction: column;

    pointer-events: auto;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      align-items: center;
      justify-content: center;

      padding: 24px;
    }
  `,
  BrandHeader: styled.div`
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;

    min-height: 48px;
    padding: 12px 0;

    background: ${Color.WHITE};

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      display: none;
    }
  `,
  BrandBar: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    font-family: 'Playfair Display', serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.95px;

    color: ${Color.GOLD_MEDIUM};
  `,
  PreferencesPanel: styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;

    display: flex;
    flex-direction: column;

    overflow: hidden;

    background: ${Color.WHITE};
    border-radius: 0;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      width: min(100%, ${DESKTOP_CARD_WIDTH});
      max-width: ${DESKTOP_CARD_WIDTH};
      height: min(632px, calc(100dvh - 96px));
      flex: 0 0 auto;

      border-radius: 24px;

      box-shadow: 0 20px 48px rgba(9, 10, 10, 0.14);
    }
  `,
  PreferencesHeader: styled.header`
    padding: 12px 16px 16px;

    background: ${Color.WHITE};

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      padding: 20px 20px 12px;
    }
  `,
  HeaderRow: styled.div`
    ${narrowContentStyles}

    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      width: auto;
      max-width: none;
      margin: 0;

      justify-content: space-between;
      gap: 16px;
    }
  `,
  PreferencesTitle: styled.h2`
    ${sectionTitleStyles}

    margin: 0;
  `,
  BrandClose: styled(BaseCloseButton)`
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);

    display: inline-flex;
    align-items: center;
    justify-content: center;
  `,
  HeaderClose: styled(BaseCloseButton)`
    display: none;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `,
  ScrollArea: styled.div`
    flex: 1;
    min-height: 0;

    overflow-y: auto;

    padding: 0 16px 24px;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      padding: 0 20px 24px;
    }
  `,
  ContentColumn: styled.div`
    ${narrowContentStyles}

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      width: auto;
      max-width: none;
      margin: 0;
    }
  `,
  IntroBlock: styled.div`
    padding-bottom: 16px;
  `,
  PreferencesText: styled.p`
    ${bodyTextStyles};
  `,
  SectionTitle: styled.h3`
    ${sectionTitleStyles}

    margin: 24px 0 12px;
  `,
  CategoryList: styled.div`
    border: 1px solid ${Color.SKY_LIGHT};
  `,
  CategoryItem: styled.div`
    position: relative;

    border-bottom: 1px solid ${Color.SKY_LIGHT};

    &:last-child {
      border-bottom: 0;
    }
  `,
  CategoryAccordion: styled.details`
    &[open] ${''}summary > span:first-child {
      background-image: url(${accordionMinusIcon});
    }
  `,
  CategoryHeader: styled.summary<{ $hasBadge?: boolean }>`
    width: 100%;
    min-height: 64px;
    position: relative;

    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;

    border: 0;
    background: ${Color.WHITE};
    text-align: left;
    cursor: pointer;

    padding: ${({ $hasBadge }) =>
      $hasBadge ? '12px 128px 12px 16px' : '12px 72px 12px 16px'};

    &::-webkit-details-marker {
      display: none;
    }
  `,
  ExpandIcon: styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    width: 20px;
    height: 20px;

    background-image: url(${accordionPlusIcon});
    background-position: center;
    background-repeat: no-repeat;
    background-size: 20px 20px;
  `,
  CategoryTitle: styled.span`
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${Color.INK_DARKEST};
  `,
  StatusBadge: styled.span`
    ${centeredRightControlStyles}

    white-space: nowrap;

    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${Color.GOLD_MEDIUM};
  `,
  Toggle: ConsentToggle,
  CategoryDescription: styled.p`
    ${bodyTextStyles}

    margin: 0;
    padding: 16px;

    border: 1px solid ${Color.SKY_LIGHT};
    background: ${Color.SKY_LIGHTER};
  `,
  PreferencesFooter: styled.div`
    border-top: 1px solid ${Color.SKY_LIGHT};
    background: ${Color.WHITE};

    padding: 12px 16px 16px;
  `,
  ActionRow: styled.div`
    ${narrowContentStyles}

    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    @media (min-width: ${MediaBreakpoint.LAPTOP}px) {
      max-width: none;

      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      align-items: center;
      gap: 10px;

      button {
        width: 100%;
      }
    }
  `,
  PrimaryButton,
  SecondaryButton,
}
